import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Dropdown from "./toolbox/Dropdown";
import { grades } from "../data/Data";
import FormGroup from "./toolbox/FormGroup";
import ValidationError from "./toolbox/ValidationError";
import style from "./css/AddSibling.module.css";
import { getBaseURL } from "./api/api";

const AddSibling = (props) => {
  const {
    adminyear: adminYear,
    data,
    parentinfo,
    addressinfo,
    counter,
    onHide,
    onClose,
    onAddSibling,
    onUpdateSibling,
    editing,
    doneEditing,
    show,
  } = props;

  const schoolYear = `${+adminYear}-${+adminYear + 1}`;

  const [gradeOptions, setGradeOptions] = useState(grades);
  const [fNameValidated, setFNameValidated] = useState(true);
  const [mNameValidated, setMNameValidated] = useState(true);
  const [lNameValidated, setLNameValidated] = useState(true);
  const [isDisabled, setisDisabled] = useState(false);
  const [fname, setFname] = useState(data[counter]?.fname || "");
  const [mname, setMname] = useState(data[counter]?.mname || "");
  const [lname, setLname] = useState(data[counter]?.lname || "");
  const [birthDate, setBirthDate] = useState(data[counter]?.birthDate || "");
  const [school, setSchool] = useState(data[counter]?.school || "");
  const [grade, setGrade] = useState(data[counter]?.grade || "");
  const [datePickerValue, setDatePickerValue] = useState(
    data[counter]?.birthDate ? new Date(data[counter].birthDate) : null
  );
  const [schoolList, setSchoolList] = useState([]);
  const dateInputContainerRef = useRef(null);

  //           ---------Minimum Date Limit-----------

  const minDate = new Date(
    new Date().getFullYear() - 22,
    new Date().getMonth(),
    new Date().getDate()
  );
  //           ---------Maximum Date Limit-----------

  const maxDate = new Date(
    new Date().getFullYear() - 4,
    new Date().getMonth(),
    new Date().getDate()
  );
  useEffect(async () => {
    const res = await axios.get(`${getBaseURL()}/schools`);
    setSchoolList(res.data);
    //console.log("res", res.data);
  }, []);

  // Only update submit button disabled state when validation changes (do not touch form fields)
  useEffect(() => {
    if (fNameValidated && mNameValidated && lNameValidated) {
      setisDisabled(true);
    } else {
      setisDisabled(false);
    }
  }, [fNameValidated, mNameValidated, lNameValidated]);

  // Sync form fields only when modal is shown or when switching add vs edit mode (not when validation state changes)
  useEffect(() => {
    if (!show) return;
    if (editing) {
      setFname(data[counter]?.fname ?? "");
      setMname(data[counter]?.mname ?? "");
      setLname(data[counter]?.lname ?? "");
      setBirthDate(data[counter]?.birthDate ?? "");
      setSchool(data[counter]?.school ?? "");
      setGrade(data[counter]?.grade ?? "");
      setDatePickerValue(
        data[counter]?.birthDate ? new Date(data[counter].birthDate) : null
      );
    } else {
      setFname("");
      setMname("");
      setLname("");
      setSchool("");
      setGrade("");
      setBirthDate("");
      setDatePickerValue(null);
    }
  }, [editing, show]);

  //          ---------- Datepicker Handler ------------------

  const handleDateChange = (selectedDate) => {
    setDatePickerValue(selectedDate);
    if (selectedDate) {
      const year = selectedDate.getFullYear();
      // getMonth() is 0-based (Jan=0, Dec=11); add 1 for calendar month 01-12
      const month = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
      const day = selectedDate.getDate().toString().padStart(2, "0");
      setBirthDate(`${year}-${month}-${day}`);
    } else {
      setBirthDate("");
    }
  };

  const parseTypedDate = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    const trimmed = raw.trim();
    const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    const [, month, day, year] = match;
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    const y = parseInt(year, 10);
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
    return date;
  };

  const handleDateBlur = (e) => {
    const raw = e?.target?.value ?? dateInputContainerRef.current?.querySelector?.("input")?.value;
    if (raw) {
      const parsed = parseTypedDate(raw);
      if (parsed) handleDateChange(parsed);
    }
  };

  const handleSchoolDropdown = (event) => {
    const newGradeOptions = grades.filter(
      (g) => g.level === event.target.value
    );
    setGradeOptions(newGradeOptions);
    setSchool(event.target.value);
  };

  const submit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity()) {
      try {
        let newValues = {
          adminYear,
          ...parentinfo,
          ...addressinfo,
          fname,
          mname,
          lname,
          school,
          grade,
          birthDate,
        };
        onAddSibling(newValues);
        onClose();
        setFname("");
        setMname("");
        setLname("");
        setSchool("");
        setGrade("");
        setBirthDate("");
      } catch {
        toast.error("Error! Re-check the form.");
      }
    }
  };

  const updateHandler = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity()) {
      try {
        let newValue = {
          ...data[counter],
          fname,
          mname,
          lname,
          school,
          grade,
          birthDate,
        };
        onUpdateSibling(counter, newValue);
        doneEditing();
        onClose();
        setFname("");
        setMname("");
        setLname("");
        setSchool("");
        setGrade("");
        setBirthDate("");
      } catch {
        toast.error("Error! Re-check the form.");
      }
    }
  };

  const fNameValidate = (e) => {
    const nameRegEx = /^[A-Za-z][A-Za-z\'\-]+([\ A-Za-z][A-Za-z\'\-]+)*$/;
    nameRegEx.test(e.target.value)
      ? setFNameValidated(true)
      : setFNameValidated(false);
  };
  const mNameValidate = (e) => {
    const nameRegEx = /^[A-Za-z][A-Za-z\'\-]+([\ A-Za-z][A-Za-z\'\-]+)*$/;
    const val = e.target.value;

    e.target.value.length > 0
      ? (val.length === 1 && nameRegEx.test(val + "a")) || nameRegEx.test(val)
        ? setMNameValidated(true)
        : setMNameValidated(false)
      : setMNameValidated(true);
  };
  const lNameValidate = (e) => {
    const nameRegEx = /^[A-Za-z][A-Za-z\'\-]+([\ A-Za-z][A-Za-z\'\-]+)*$/;
    nameRegEx.test(e.target.value)
      ? setLNameValidated(true)
      : setLNameValidated(false);
  };

  return (
    <div>
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton className={style.title}>
          {counter != 0 ? "Sibling " + counter + " - " : ""}Student Information
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={editing ? updateHandler : submit}>
            <Form.Row
              className={`justify-content-between flex-nowrap m-auto ${style.row}`}
            >
              <Col>
                <FormGroup
                  id="fname"
                  type="text"
                  value={fname}
                  onChange={(e) => setFname(e.target.value)}
                  label="First Name"
                  placeholder=""
                  // value={fname}
                  onBlur={fNameValidate}
                  required={true}
                />
                {!fNameValidated && <ValidationError field="First Name" />}
              </Col>
              <Col>
                <FormGroup
                  id="mname"
                  type="text"
                  value={mname}
                  onChange={(e) => setMname(e.target.value)}
                  label="Middle Name (optional)"
                  required={false}
                  onBlur={mNameValidate}
                />
                {!mNameValidated && <ValidationError field="Middle Name" />}
              </Col>
              <Col>
                <FormGroup
                  id="lname"
                  type="text"
                  value={lname}
                  onChange={(e) => setLname(e.target.value)}
                  label="Last Name"
                  placeholder=""
                  onBlur={lNameValidate}
                  required
                />
                {!lNameValidated && <ValidationError field="Last Name" />}
              </Col>
            </Form.Row>
            <Form.Row
              className={`justify-content-between flex-nowrap m-auto ${style.row}`}
            >
              <Col>
                <Form.Label>Date of birth</Form.Label>
                <div ref={dateInputContainerRef}>
                  <DatePicker
                    id="birthDate"
                    selected={datePickerValue}
                    onChange={handleDateChange}
                    onBlur={handleDateBlur}
                    minDate={minDate}
                  maxDate={maxDate}
                  placeholderText="MM/DD/YYYY"
                  dateFormat="MM/dd/yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  className="form-control"
                  required
                  popperPlacement="auto"
                  popperModifiers={[
                    {
                      name: "preventOverflow",
                      options: { enabled: true, boundariesElement: "viewport" },
                    },
                    { name: "offset", options: { offset: [0, 5] } },
                  ]}
                  />
                </div>
              </Col>

              <Col>
                <Dropdown
                  id="school"
                  value={school}
                  onChange={handleSchoolDropdown}
                  label={`School (${schoolYear})`}
                  defaultVal="School"
                  options={schoolList}
                />
              </Col>
              <Col>
                <Dropdown
                  id="grade"
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  label={"Grade (" + schoolYear + ")"}
                  defaultVal="Grade"
                  options={gradeOptions}
                />
              </Col>
            </Form.Row>
            <ToastContainer />
            <Row className="d-flex justify-content-around mt-4">
              {editing ? (
                <>
                  <Button
                    className={style.button}
                    type="submit"
                    disabled={!isDisabled}
                    // onClick={updateHandler}
                  >
                    Update
                  </Button>
                  <Button className={style.button} onClick={onClose}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    className={style.button}
                    type="submit"
                    disabled={!isDisabled}
                    // onClick={submit}
                  >
                    Add Sibling
                  </Button>
                  <Button className={style.button} onClick={onHide}>
                    Cancel
                  </Button>
                </>
              )}
            </Row>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AddSibling;
